/**
 * excelExport.js
 *
 * Generates a human-readable multi-sheet Excel workbook from project data.
 *
 * Sheet layout:
 *   1. Account Data              — one row per account, month values spread across columns
 *   2. Changes                   — one row per change, all metadata fields
 *   3. Reference Accounts        — one row per reference account, with monthly forecast
 *                                  values spread across month columns
 *   4. Pro Forma Monthly Values  — explicit month-by-month forecast values for pro forma
 *                                  reference accounts (change type: account + is_stat_driver)
 *   5. Stat Driver Relationships — driver → driven account mappings for stat driver changes
 *   6. Change Dates              — computed per-GL-key monthly distribution for master stat
 *                                  and other changes that store a dates object
 *
 * The Account Data sheet is intentionally compatible with the existing new-project
 * upload template so the workbook can be re-uploaded to create a new project.
 */

import xlsx from 'json-as-xlsx';
import { format } from 'date-fns';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Convert a dates JSONB object { "2024-01-01": 1000, ... } into a flat map
 * keyed by "MMM yyyy" (e.g. { "Jan 2024": 1000, ... }).
 *
 * Uses T00:00:00 suffix to force local-time parsing and avoid the UTC midnight
 * timezone shift that affects bare ISO date strings in negative-UTC-offset timezones.
 */
function flattenDates(datesObj) {
  if (!datesObj || typeof datesObj !== 'object') return {};
  const result = {};
  for (const [isoKey, value] of Object.entries(datesObj)) {
    try {
      const d = new Date(`${isoKey}T00:00:00`);
      if (!isNaN(d.getTime())) {
        result[format(d, 'MMM yyyy')] = value ?? '';
      }
    } catch {
      // skip unparseable keys
    }
  }
  return result;
}

/**
 * Collect the full ordered set of month labels across all accounts.
 * Returns an array like ["Jan 2024", "Feb 2024", ...].
 */
function collectMonthColumns(accounts) {
  const seen = new Map();
  for (const acct of accounts) {
    const flat = flattenDates(acct.dates);
    for (const label of Object.keys(flat)) {
      if (!seen.has(label)) {
        try {
          const parts = label.split(' ');
          const d = new Date(`${parts[0]} 1, ${parts[1]}`);
          seen.set(label, isNaN(d.getTime()) ? new Date(0) : d);
        } catch {
          seen.set(label, new Date(0));
        }
      }
    }
  }
  return [...seen.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([label]) => label);
}

/**
 * Collect the ordered set of month_year strings from reference account monthly_values
 * across all changes. Returns sorted "MMM yyyy" strings.
 */
function collectRefMonthColumns(changes) {
  const seen = new Map();
  for (const change of changes) {
    for (const ref of (change.reference_accounts ?? [])) {
      for (const mv of (ref.monthly_values ?? [])) {
        const label = mv.month_year;
        if (label && !seen.has(label)) {
          try {
            const parts = label.split(' ');
            const d = new Date(`${parts[0]} 1, ${parts[1]}`);
            seen.set(label, isNaN(d.getTime()) ? new Date(0) : d);
          } catch {
            seen.set(label, new Date(0));
          }
        }
      }
    }
  }
  return [...seen.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([label]) => label);
}

/**
 * Format a date_range array into a readable string.
 * e.g. ["2024-01-01", "2024-12-01"] → "Jan 2024 - Dec 2024"
 */
function formatDateRange(dateRange) {
  if (!Array.isArray(dateRange) || dateRange.length < 2) return '';
  try {
    const fmt = (d) => {
      const date = typeof d === 'string' ? new Date(`${d}T00:00:00`) : new Date(d);
      return format(date, 'MMM yyyy');
    };
    return `${fmt(dateRange[0])} - ${fmt(dateRange[dateRange.length - 1])}`;
  } catch {
    return String(dateRange);
  }
}

/**
 * Safely stringify an array field for display in a cell.
 */
function arrayToString(val) {
  if (!val) return '';
  if (Array.isArray(val)) return val.join(', ');
  return String(val);
}

// ─── Sheet builders ──────────────────────────────────────────────────────────

function buildAccountDataSheet(accounts) {
  const monthCols = collectMonthColumns(accounts);

  const columns = [
    { label: 'Entity ID',         value: 'Entity ID' },
    { label: 'Entity',            value: 'Entity' },
    { label: 'Department ID',     value: 'Department ID' },
    { label: 'Department Name',   value: 'Department Name' },
    { label: 'Account ID',        value: 'Account ID' },
    { label: 'Account Name',      value: 'Account Name' },
    { label: 'Parent Account',    value: 'Parent Account' },
    { label: 'Acct Type',         value: 'Acct Type' },
    { label: 'Fixed or Variable', value: 'Fixed or Variable' },
    { label: 'Driver',            value: 'Driver' },
    ...monthCols.map(m => ({ label: m, value: m })),
  ];

  const content = accounts.map(acct => {
    const flat = flattenDates(acct.dates);
    const row = {
      'Entity ID':          acct.entity_id        ?? '',
      'Entity':             acct.entity           ?? '',
      'Department ID':      acct.department_id    ?? '',
      'Department Name':    acct.department_name  ?? '',
      'Account ID':         acct.account_id       ?? '',
      'Account Name':       acct.account_name     ?? '',
      'Parent Account':     acct.parent_account   ?? '',
      'Acct Type':          acct.acct_type        ?? '',
      'Fixed or Variable':  acct.fixed_or_variable ?? '',
      'Driver':             acct.driver           ?? '',
    };
    for (const m of monthCols) {
      row[m] = flat[m] ?? '';
    }
    return row;
  });

  return { sheet: 'Account Data', columns, content };
}

function buildChangesSheet(changes) {
  const columns = [
    { label: 'Change Label',          value: 'Change Label' },
    { label: 'Function Operator',     value: 'Function Operator' },
    { label: 'Operator Value',        value: 'Operator Value' },
    { label: 'Date Range',            value: 'Date Range' },
    { label: 'Date Range Select',     value: 'Date Range Select' },
    { label: 'Selected Accounts',     value: 'Selected Accounts' },
    { label: 'Parent Change',         value: 'Parent Change' },
    { label: 'Is Dominant Change',    value: 'Is Dominant Change' },
    { label: 'Is Stat Driver',        value: 'Is Stat Driver' },
    { label: 'Is Master Stat Change', value: 'Is Master Stat Change' },
    { label: 'Affects Stat Drivers',  value: 'Affects Stat Drivers' },
    { label: 'Stat Driver GL Key',    value: 'Stat Driver GL Key' },
    { label: 'Driven Accounts',       value: 'Driven Accounts' },
    { label: 'Ref Account Formula',   value: 'Ref Account Formula' },
    { label: 'User Note',             value: 'User Note' },
  ];

  const content = changes.map(c => {
    // Collect all driven account GL keys across all stat drivers on this change
    const drivenGlKeys = [];
    if (c.affects_stat_drivers && Array.isArray(c.affected_stat_drivers)) {
      for (const driver of c.affected_stat_drivers) {
        for (const da of (driver.drivenAccounts ?? [])) {
          if (da.gl_key) drivenGlKeys.push(da.gl_key);
        }
      }
    }

    // Identify stat driver GL key (reference account flagged is_stat_driver,
    // or stat_driver_gl_key if present, or rowLetter 'A' fallback)
    let statDriverGlKey = c.stat_driver_gl_key ?? '';
    if (!statDriverGlKey && Array.isArray(c.reference_accounts)) {
      const sdRef = c.reference_accounts.find(ra => ra.is_stat_driver)
        ?? (c.reference_accounts[0]?.rowLetter === 'A' ? c.reference_accounts[0] : null);
      if (sdRef) statDriverGlKey = sdRef.glKey ?? '';
    }

    return {
      'Change Label':          c.change_label          ?? '',
      'Function Operator':     c.function_operator     ?? '',
      'Operator Value':        c.operator_value        ?? '',
      'Date Range':            formatDateRange(c.date_range),
      'Date Range Select':     c.date_range_select     ?? 'custom',
      'Selected Accounts':     arrayToString(c.selected_accounts),
      'Parent Change':         c.parent_change         ?? '',
      'Is Dominant Change':    c.is_dominant_change    ? 'TRUE' : 'FALSE',
      'Is Stat Driver':        c.is_stat_driver        ? 'TRUE' : 'FALSE',
      'Is Master Stat Change': c.is_master_stat_change ? 'TRUE' : 'FALSE',
      'Affects Stat Drivers':  c.affects_stat_drivers  ? 'TRUE' : 'FALSE',
      'Stat Driver GL Key':    statDriverGlKey,
      'Driven Accounts':       drivenGlKeys.join(', '),
      'Ref Account Formula':   c.ref_account_formula   ?? '',
      'User Note':             c.user_note             ?? '',
    };
  });

  return { sheet: 'Changes', columns, content };
}

/**
 * Reference Accounts sheet — one row per reference account entry.
 * Monthly forecast values are spread across month columns so every
 * per-account per-month value is visible (critical for pro forma changes).
 */
function buildReferenceAccountsSheet(changes) {
  const monthCols = collectRefMonthColumns(changes);

  const columns = [
    { label: 'Change Label',   value: 'Change Label' },
    { label: 'Row Letter',     value: 'Row Letter' },
    { label: 'GL Key',         value: 'GL Key' },
    { label: 'Is Fixed Value', value: 'Is Fixed Value' },
    { label: 'Is Stat Driver', value: 'Is Stat Driver' },
    ...monthCols.map(m => ({ label: m, value: m })),
  ];

  const content = [];
  for (const change of changes) {
    const refs = change.reference_accounts ?? [];
    for (const ref of refs) {
      // Build a month → value map from monthly_values array
      const monthMap = {};
      for (const mv of (ref.monthly_values ?? [])) {
        if (mv.month_year) monthMap[mv.month_year] = mv.value ?? '';
      }

      const row = {
        'Change Label':   change.change_label ?? '',
        'Row Letter':     ref.rowLetter       ?? '',
        'GL Key':         ref.glKey           ?? '',
        'Is Fixed Value': ref.is_fixed_value  ? 'TRUE' : 'FALSE',
        'Is Stat Driver': ref.is_stat_driver  ? 'TRUE' : 'FALSE',
      };
      for (const m of monthCols) {
        row[m] = monthMap[m] ?? '';
      }
      content.push(row);
    }
  }

  return { sheet: 'Reference Accounts', columns, content };
}

/**
 * Pro Forma Monthly Values sheet — explicit per-account forecast values for
 * pro forma changes (function_operator = 'account' and is_stat_driver = true).
 * One row per reference account per change, with each month as its own column.
 * Includes whether each month value was manually entered vs. calculated.
 */
function buildProFormaMonthlyValuesSheet(changes) {
  const proFormaChanges = changes.filter(
    c => c.function_operator === 'account' && c.is_stat_driver
  );

  if (proFormaChanges.length === 0) return null;

  const monthCols = collectRefMonthColumns(proFormaChanges);

  const columns = [
    { label: 'Change Label',     value: 'Change Label' },
    { label: 'Row Letter',       value: 'Row Letter' },
    { label: 'GL Key',           value: 'GL Key' },
    { label: 'Is Fixed Value',   value: 'Is Fixed Value' },
    { label: 'Is Stat Driver',   value: 'Is Stat Driver' },
    { label: 'Period Total',     value: 'Period Total' },
    ...monthCols.map(m => ({ label: m, value: m })),
    ...monthCols.map(m => ({ label: `${m} (Manual)`, value: `${m} (Manual)` })),
  ];

  const content = [];
  for (const change of proFormaChanges) {
    for (const ref of (change.reference_accounts ?? [])) {
      const monthMap = {};
      const manualMap = {};
      let periodTotal = 0;

      for (const mv of (ref.monthly_values ?? [])) {
        if (mv.month_year) {
          const val = mv.value ?? 0;
          monthMap[mv.month_year] = val;
          manualMap[mv.month_year] = mv.is_manually_entered ? 'YES' : '';
          periodTotal += Number(val) || 0;
        }
      }

      const row = {
        'Change Label':   change.change_label ?? '',
        'Row Letter':     ref.rowLetter       ?? '',
        'GL Key':         ref.glKey           ?? '',
        'Is Fixed Value': ref.is_fixed_value  ? 'TRUE' : 'FALSE',
        'Is Stat Driver': ref.is_stat_driver  ? 'TRUE' : 'FALSE',
        'Period Total':   periodTotal || '',
      };
      for (const m of monthCols) {
        row[m] = monthMap[m] ?? '';
        row[`${m} (Manual)`] = manualMap[m] ?? '';
      }
      content.push(row);
    }
  }

  return { sheet: 'Pro Forma Monthly Values', columns, content };
}

/**
 * Stat Driver Relationships sheet — for changes that affect stat drivers,
 * shows which driver accounts drive which driven accounts.
 */
function buildStatDriverRelationshipsSheet(changes) {
  const statDriverChanges = changes.filter(
    c => c.affects_stat_drivers && Array.isArray(c.affected_stat_drivers) && c.affected_stat_drivers.length > 0
  );

  if (statDriverChanges.length === 0) return null;

  const columns = [
    { label: 'Change Label',       value: 'Change Label' },
    { label: 'Driver GL Key',      value: 'Driver GL Key' },
    { label: 'Driven Account GL Key', value: 'Driven Account GL Key' },
    { label: 'Driven Entity ID',   value: 'Driven Entity ID' },
    { label: 'Driven Department ID', value: 'Driven Department ID' },
    { label: 'Driven Account ID',  value: 'Driven Account ID' },
  ];

  const content = [];
  for (const change of statDriverChanges) {
    for (const driver of (change.affected_stat_drivers ?? [])) {
      for (const da of (driver.drivenAccounts ?? [])) {
        content.push({
          'Change Label':            change.change_label    ?? '',
          'Driver GL Key':           driver.gl_key          ?? '',
          'Driven Account GL Key':   da.gl_key              ?? '',
          'Driven Entity ID':        da.entity_id           ?? '',
          'Driven Department ID':    da.department_id       ?? '',
          'Driven Account ID':       da.account_id          ?? '',
        });
      }
    }
  }

  return { sheet: 'Stat Driver Relationships', columns, content };
}

/**
 * Change Dates sheet — for changes that store a computed `dates` object
 * (master stat distribution, calculated accounts, etc.).
 * The dates object shape is: { glKey: { "MMM yyyy": value, ... }, ... }
 * One row per GL key per change.
 */
function buildChangeDatesSheet(changes) {
  const changesWithDates = changes.filter(
    c => c.dates && typeof c.dates === 'object' && Object.keys(c.dates).length > 0
  );

  if (changesWithDates.length === 0) return null;

  // Collect all month keys across all dates objects
  const seen = new Map();
  for (const change of changesWithDates) {
    for (const monthMap of Object.values(change.dates)) {
      if (monthMap && typeof monthMap === 'object') {
        for (const monthKey of Object.keys(monthMap)) {
          if (!seen.has(monthKey)) {
            try {
              const parts = monthKey.split(' ');
              const d = new Date(`${parts[0]} 1, ${parts[1]}`);
              seen.set(monthKey, isNaN(d.getTime()) ? new Date(0) : d);
            } catch {
              seen.set(monthKey, new Date(0));
            }
          }
        }
      }
    }
  }
  const monthCols = [...seen.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([label]) => label);

  const columns = [
    { label: 'Change Label',    value: 'Change Label' },
    { label: 'Change Type',     value: 'Change Type' },
    { label: 'GL Key',          value: 'GL Key' },
    ...monthCols.map(m => ({ label: m, value: m })),
  ];

  const content = [];
  for (const change of changesWithDates) {
    const changeType = change.is_master_stat_change ? 'Master Stat'
      : change.function_operator === 'calculatedAccount' ? 'Calculated Account'
      : change.function_operator === 'recursiveCalculatedAccount' ? 'Recursive Calc Account'
      : change.function_operator ?? '';

    for (const [glKey, monthMap] of Object.entries(change.dates)) {
      if (!monthMap || typeof monthMap !== 'object') continue;

      const row = {
        'Change Label': change.change_label ?? '',
        'Change Type':  changeType,
        'GL Key':       glKey,
      };
      for (const m of monthCols) {
        // monthMap values may be plain numbers or { value, ... } objects
        const raw = monthMap[m];
        row[m] = raw !== undefined && raw !== null
          ? (typeof raw === 'object' ? (raw.value ?? '') : raw)
          : '';
      }
      content.push(row);
    }
  }

  return { sheet: 'Change Dates', columns, content };
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Generate and download a multi-sheet Excel workbook.
 *
 * @param {Object} params
 * @param {Array}  params.accounts     - project_data_accounts array (with dates)
 * @param {Array}  params.changes      - changes array (each may have reference_accounts[])
 * @param {string} params.projectTitle - used in the filename
 */
export function exportProjectToExcel({ accounts, changes, projectTitle }) {
  const safeTitle = (projectTitle || 'project')
    .replace(/[^a-zA-Z0-9_\- ]/g, '')
    .trim()
    .replace(/\s+/g, '_');

  const timestamp = format(new Date(), 'yyyy-MM-dd');
  const fileName = `${safeTitle}_export_${timestamp}`;

  // Build all sheets; optional sheets are only added if they have relevant data
  const optionalSheets = [
    buildProFormaMonthlyValuesSheet(changes),
    buildStatDriverRelationshipsSheet(changes),
    buildChangeDatesSheet(changes),
  ].filter(Boolean);

  const sheets = [
    buildAccountDataSheet(accounts),
    buildChangesSheet(changes),
    buildReferenceAccountsSheet(changes),
    ...optionalSheets,
  ];

  xlsx(sheets, {
    fileName,
    extraLength: 5,
    writeOptions: {},
  });
}
